import {
  Alert,
  Box,
  FormControlLabel,
  FormGroup,
  IconButton,
  LinearProgress,
  OutlinedInput,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import React, { ChangeEvent, useCallback, useState } from "react";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import {
  useCreateMessageMutation,
  useGenerateSuggestionQuery,
} from "src/api/services/messagesApi";
import { isError } from "src/utils/isError";
import { useParams } from "react-router-dom";
import { MatchParam } from "src/constants/matches";
import { capitalize } from "src/utils/capitalize";
import { skipToken } from "@reduxjs/toolkit/query";
import AlertWithCopy from "src/components/AlertWithCopy";
import MessageSuggestion from "src/shared/MessageSuggestion";

interface Props {
  onSuccess?: () => void;
  threadSlug: string;
}

const NewMessageForm = ({ onSuccess, threadSlug }: Props) => {
  const { queue } = useParams<MatchParam<"QUEUE_TYPE">>();
  const language = useClinician((c) => c.language);
  const [error, setError] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [createMessage] = useCreateMessageMutation();
  const [slug, setSlug] = useState<string | null>(null);
  const { data, refetch, isLoading, isFetching } = useGenerateSuggestionQuery(
    slug ?? skipToken,
  );
  const [requestSuggestion, setRequestSuggestion] = useState<boolean>(false);
  const [require, setRequire] = useState(false);
  const [isPHI, setIsPHI] = useState(false);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setError("");
    setNewMessage(event.target.value);
  }, []);

  const handlePHI = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setIsPHI(e.target.checked),
    [],
  );

  const handleRequired = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setRequire(e.target.checked),
    [],
  );

  const handleGenerateSuggestion = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      setRequestSuggestion(true);
      setSlug(threadSlug);
    },
    [threadSlug],
  );

  const handleCloseSuggestion = useCallback(() => {
    setSlug(null);
    setRequestSuggestion(false);
  }, []);

  const sendMessage = useCallback(() => {
    if (threadSlug == null) return;

    createMessage({
      thread_slug: threadSlug,
      message: newMessage,
      require_response: require,
      language,
      is_phi: isPHI,
      current_queue: queue ? capitalize(queue) : undefined,
    })
      .unwrap()
      .catch((err) => {
        if (isError(err)) {
          setError(err.data.message);
        }
        console.error(err);
      })
      .then(() => {
        setNewMessage("");
        setRequire(false);
        setIsPHI(false);
        onSuccess?.();
      });
  }, [
    createMessage,
    isPHI,
    language,
    newMessage,
    onSuccess,
    queue,
    require,
    threadSlug,
  ]);

  return (
    <Stack>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <MessageSuggestion
          generate={handleGenerateSuggestion}
          onClose={handleCloseSuggestion}
          generated={!!data}
          isLoading={isLoading}
          refetch={refetch}
          requestSuggestion={requestSuggestion}
        />
        <Stack>
          <FormGroup sx={{ width: "190px" }}>
            <FormControlLabel
              sx={{ margin: 0 }}
              control={
                <Switch
                  color="secondary"
                  checked={isPHI}
                  onChange={handlePHI}
                />
              }
              label="Is PHI"
              labelPlacement="start"
            />
            <FormControlLabel
              sx={{ margin: 0 }}
              control={<Switch checked={require} onChange={handleRequired} />}
              label="Require response"
              labelPlacement="start"
            />
          </FormGroup>
        </Stack>
      </Stack>
      {requestSuggestion && data != null ? (
        <Box mt={1} mb={1}>
          {isFetching ? (
            <Stack>
              <LinearProgress />
              <Typography>Generating new suggestion...</Typography>
            </Stack>
          ) : (
            <AlertWithCopy text={data} copyText={data} />
          )}
        </Box>
      ) : null}
      <Stack gap={0}>
        <OutlinedInput
          error={error.length > 0}
          value={newMessage}
          onChange={handleChange}
          id="outlined-adornment"
          type="text"
          multiline
          maxRows={10}
          placeholder="Type here..."
          sx={{
            background: "#F8F8F8",
            borderRadius: "10px",
            width: "100%",
            color: "#000",
            gap: "1rem",
            padding: "10px 15px",
            mt: "auto",
          }}
          endAdornment={
            <IconButton
              sx={{ color: "#00B6B9" }}
              onClick={sendMessage}
              disabled={newMessage.length === 0}
            >
              <SendIcon />
            </IconButton>
          }
        />
        {error ? (
          <Typography color="error" variant="caption" ml={1}>
            {error}
          </Typography>
        ) : null}
        {queue == null ? (
          <Alert severity="warning">
            When messaging outside of a task queue, tasks will not be completed
          </Alert>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default NewMessageForm;
